import React, { useEffect, useState, useRef } from 'react';
import { X, Trophy } from 'lucide-react';
import { useLang } from '../utils/i18n';

interface WinnerBannerProps {
  winner: string;
  winnerImage?: string;
  onDismiss: () => void;
}

/**
 * Non-blocking winner announcement that overlays the wheel card.
 *
 * Shows the winner's name for 3 seconds, then auto-fades and calls onDismiss.
 * A small "×" lets the user dismiss early. Crucially, this is NOT a modal — the
 * spin button stays clickable underneath, so a teacher running cold-calls can
 * fire the next spin without waiting or clicking anything.
 *
 * Confetti is handled separately by HomePage (it's already a fixed overlay), so
 * this component doesn't need to know about it.
 */
const DISPLAY_MS = 3000;
const FADEOUT_MS = 250; // matches .animate-banner-out duration

const WinnerBanner: React.FC<WinnerBannerProps> = ({ winner, winnerImage, onDismiss }) => {
  const { t } = useLang();
  const [fading, setFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Start the auto-dismiss countdown.
    timerRef.current = setTimeout(() => {
      setFading(true);
      fadeTimerRef.current = setTimeout(onDismiss, FADEOUT_MS);
    }, DISPLAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [onDismiss]);

  const handleDismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setFading(true);
    fadeTimerRef.current = setTimeout(onDismiss, FADEOUT_MS);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`
        absolute top-3 left-1/2 -translate-x-1/2 z-20
        flex items-center gap-2.5 px-5 py-2.5
        rounded-2xl shadow-lg
        bg-white/90 dark:bg-gray-800/90 backdrop-blur-md
        border border-violet-200 dark:border-violet-700
        ${fading ? 'animate-banner-out' : 'animate-banner-in'}
      `}
      style={{ maxWidth: 'calc(100% - 1.5rem)' }}
    >
      {winnerImage ? (
        <img
          src={winnerImage}
          alt=""
          className="h-10 w-10 flex-shrink-0 rounded-xl object-cover shadow-md ring-1 ring-violet-200 dark:ring-violet-700"
          onError={event => { event.currentTarget.hidden = true; }}
        />
      ) : (
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-md">
          <Trophy size={16} className="text-white" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-violet-500 dark:text-violet-400 uppercase tracking-wider leading-none mb-0.5">
          {t('wheel.winner')}
        </p>
        <p className="text-sm font-extrabold text-gray-900 dark:text-white truncate leading-tight">
          <bdi>{winner}</bdi>
        </p>
      </div>

      <button
        onClick={handleDismiss}
        aria-label={t('wheel.dismissWinner')}
        className="flex-shrink-0 p-1 text-gray-300 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <X size={14} />
      </button>

      {/* Auto-dismiss countdown bar */}
      <span
        className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-violet-400/50"
        aria-hidden="true"
      >
        <span
          className="block h-full rounded-full bg-violet-500 animate-shrink"
          style={{ animationDuration: `${DISPLAY_MS}ms` }}
        />
      </span>
    </div>
  );
};

export default WinnerBanner;
